import styled, { css } from 'styled-components';
import { memo, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { parse } from 'hub-http/helpers/params';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILink from 'UIComponents/link/UILink';
import UISection from 'UIComponents/section/UISection';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import { CALYPSO_DARK, CALYPSO_LIGHT, CALYPSO_MEDIUM, EERIE } from 'HubStyleTokens/colors';
import { getCallBody, getCRMObjectId, getResult, isResultSuccessful } from '../call-message/operators/callMessageGetters';
import CallMetadataError from './CallMetadataError';
import CallNotesTextEditor from './CallNotesTextEditor';
import { getId } from 'conversations-message-history/common-message-format/operators/commonMessageGetters';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LightGrayFormattedMessage = styled(FormattedMessage).withConfig({
  displayName: "MutableCallNotes__LightGrayFormattedMessage",
  componentId: "sc-1y2ju0w-0"
})(["color:", ";"], EERIE);
const sectionHoverStyles = css(["background-color:", ";border:1px solid ", ";border-radius:3px;cursor:text;padding:8px;position:relative;&&{margin:-9px -9px 7px -9px;}"], CALYPSO_LIGHT, CALYPSO_MEDIUM);
const StyledUISection = styled(UISection).withConfig({
  displayName: "MutableCallNotes__StyledUISection",
  componentId: "sc-1y2ju0w-1"
})(["", ";"], ({
  isHovering
}) => isHovering && sectionHoverStyles);
const iconHoverStyles = css(["bottom:0;position:absolute;right:0;"]);
const StyledUIIcon = styled(UIIcon).withConfig({
  displayName: "MutableCallNotes__StyledUIIcon",
  componentId: "sc-1y2ju0w-2"
})(["&&{display:", ";}", ";"], ({
  isHovering
}) => !isHovering && 'none', ({
  isHovering
}) => isHovering && iconHoverStyles);
const MutableCallNotes = ({
  callMessage,
  handleEngagementDataUpdated,
  message,
  resetCallMessage
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const callBody = getCallBody(callMessage);
  const callBodyResult = callBody && getResult(callBody);
  const [previewTextHtmlString, setPreviewTextHtmlString] = useState(callBodyResult);
  const engagementId = getCRMObjectId(callMessage);
  const messageId = getId(message);
  const history = useHistory();
  const parser = new DOMParser();
  const previewTextFullHtml = parser.parseFromString(previewTextHtmlString, 'text/html');
  const previewTextBodyHtml = previewTextFullHtml.body;
  const previewText = previewTextBodyHtml.innerText;
  useEffect(() => {
    setPreviewTextHtmlString(callBodyResult);
  }, [callBodyResult]);
  useEffect(() => {
    const queryParams = parse(history.location.search.substring(1));
    if (queryParams && queryParams.focusCallNotesEditor && queryParams.messageId === messageId) {
      setIsEditing(true);
      return () => {
        history.replace({
          search: ''
        });
      };
    }
  }, [history, messageId]);
  if (!isResultSuccessful(callBody)) {
    return /*#__PURE__*/_jsx(CallMetadataError, {
      className: "m-bottom-3",
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "conversations-calling-message.timeline-call-metadata.call-notes.error_jsx",
        options: {
          onClick: resetCallMessage
        },
        elements: {
          UILink
        }
      })
    });
  }
  const handleToggleTextEditor = () => {
    setIsEditing(!isEditing);
    setIsHovering(false);
  };
  const handleMouseEnter = () => {
    setIsHovering(true);
  };
  const handleMouseLeave = () => {
    setIsHovering(false);
  };
  return isEditing ? /*#__PURE__*/_jsx(CallNotesTextEditor, {
    engagementId: engagementId,
    handleEngagementDataUpdated: handleEngagementDataUpdated,
    previewTextHtmlString: previewTextHtmlString,
    setIsEditing: setIsEditing
  }) : /*#__PURE__*/_jsx(StyledUISection, {
    "data-test-id": "call-notes",
    isHovering: isHovering,
    onClick: handleToggleTextEditor,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    children: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(StyledUIIcon, {
        name: "edit",
        "data-test-id": "edit-notes-icon",
        className: "p-right-2 p-bottom-2",
        color: CALYPSO_DARK,
        isHovering: isHovering
      }), previewTextHtmlString && previewText.length > 0 ? /*#__PURE__*/_jsx(SanitizedText, {
        body: previewTextHtmlString
      }) : /*#__PURE__*/_jsx(LightGrayFormattedMessage, {
        message: "conversations-calling-message.call-notes-editor.add-notes-placeholder"
      })]
    })
  });
};
export default /*#__PURE__*/memo(MutableCallNotes);